import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { APIProvider, Map as GMap, Marker, useMap } from "@vis.gl/react-google-maps";
import {
  Search, Crosshair, MapPin, Star, Clock, Store, BadgeCheck, ArrowRight,
  ExternalLink, Grid3X3, SlidersHorizontal, Map as MapIcon, Compass, Layers, CalendarCheck, Gauge, ShieldCheck,
} from "lucide-react";
import { Reveal } from "../components/ui-bits";
import { aggregatorApi } from "../lib/api";

const NOIDA_CENTER = { lat: 28.565, lng: 77.431 };
const QUICK_AREAS = ["Sector 119", "Sector 120", "Sector 121", "Sector 122", "Cleo County", "Homes 121", "Eldeco Magnolia Park"];

const DEMO_CENTRES = [
  { id: "demo-1", name: "VS Motors", area: "Sector 119, Noida" },
  { id: "demo-2", name: "Sparkle Shine Car Care & Detailing Center", area: "Sector 121, Noida" },
  { id: "demo-3", name: "Team Car Craft", area: "Sector 122, Noida" },
  { id: "demo-4", name: "Kishor Car Wash & Detailing Hub", area: "Sector 122, Noida" },
  { id: "demo-5", name: "Detailing in Vogue", area: "HOMES-121, Sector 121, Noida" },
  { id: "demo-6", name: "Prince Car Care", area: "Sector 121, Noida" },
  { id: "demo-7", name: "Shri Shyam Car Washing Centre", area: "Sector 121, Noida" },
  { id: "demo-8", name: "CarX Auto Services", area: "Sector 122, Noida" },
].map((c) => ({ ...c, category: "Car Wash", carza_partner: false, demo: true }));

const hasKey = () => {
  const k = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  return !!k && k.length > 10 && !k.includes("YOUR_");
};

function haversine(a, b) {
  const R = 6371, dLat = ((b.lat - a.lat) * Math.PI) / 180, dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

const pinIcon = (selected, partner) => ({
  url:
    "data:image/svg+xml," +
    encodeURIComponent(
      `<svg width="${selected ? 44 : 34}" height="${selected ? 44 : 34}" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C7.9 2 4.5 5.4 4.5 9.5c0 5.2 6.4 11.6 6.7 11.9.4.4 1.2.4 1.6 0 .3-.3 6.7-6.7 6.7-11.9C19.5 5.4 16.1 2 12 2z" fill="${partner ? "#6D3DF5" : "#5b5470"}" stroke="#ffffff" stroke-width="1.6"/><circle cx="12" cy="9.5" r="3" fill="#ffffff"/></svg>`
    ),
  anchor: { x: (selected ? 44 : 34) / 2, y: selected ? 44 : 34 },
});

export default function AggregatorMapSection({ onSelectCentre }) {
  const [center, setCenter] = useState(NOIDA_CENTER);
  const [results, setResults] = useState([]);
  const [bookable, setBookable] = useState([]);
  const [demo, setDemo] = useState(!hasKey());
  const [loading, setLoading] = useState(hasKey());
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("nearest");
  const [origin, setOrigin] = useState(NOIDA_CENTER);
  const listRef = useRef(null);
  const cardRefs = useRef({});
  const panRef = useRef(null);

  // centres with configured slot booking come from the CARZA database
  useEffect(() => {
    aggregatorApi.centres().then((r) => setBookable((r.centres || []).map((c) => ({
      id: c.centre_code, centre_code: c.centre_code, name: c.name, area: c.area || c.address, address: c.address,
      lat: c.lat, lng: c.lng, phone: c.phone,
      category: "Car Wash & Detailing Centre",
      services: c.services || [], vehicle_categories: c.vehicle_categories || [],
      booking_mode: c.booking_mode, availability_configured: c.availability_configured,
      carza_partner: true, bookable: true, demo: c.is_demo,
    })))).catch(() => {});
  }, []);

  const load = async (c) => {
    if (!hasKey()) { setDemo(true); return; }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/places/nearby?lat=${c.lat}&lng=${c.lng}&radius=2500`).then((r) => r.json());
      setResults(res.results || []);
      setDemo(!!res.demo);
    } catch (e) {
      setError("Could not load nearby centres. Please retry.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(NOIDA_CENTER); }, []);

  const useMyLocation = () => {
    if (!navigator.geolocation) return setError("Location is not available on this device.");
    setError("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCenter(c); setOrigin(c); load(c);
      },
      () => setError("Location permission denied. Search your society instead."),
      { timeout: 8000 }
    );
  };

  const searchArea = async (q) => {
    const term = (q ?? query).trim();
    if (!term) return;
    if (!hasKey()) { setDemo(true); setError(""); return; }
    setSearching(true);
    setError("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/places/search?q=${encodeURIComponent(term)}`).then((r) => r.json());
      if (res.ok && res.center) {
        setCenter(res.center); setOrigin(res.center); load(res.center);
      } else {
        setError(res.detail || `Could not find '${term}'. Try a nearby sector or society.`);
      }
    } catch {
      setError("Search failed. Please try again.");
    } finally {
      setSearching(false);
    }
  };

  const visible = useMemo(() => {
    let list = [...bookable, ...(demo ? DEMO_CENTRES : results)];
    if (filter === "wash") list = list.filter((r) => (r.category || "").toLowerCase().includes("wash") || (r.services || []).some((s) => s.toLowerCase().includes("wash")));
    if (filter === "detailing") list = list.filter((r) => (r.category || "").toLowerCase().includes("detail") || (r.services || []).some((s) => s.toLowerCase().includes("detail")));
    if (filter === "verified") list = list.filter((r) => r.carza_partner);
    if (filter === "bookable") list = list.filter((r) => r.bookable);
    if (filter === "open") list = list.filter((r) => r.open_now === true);
    list = list.map((r) => ({ ...r, distance: r.lat && r.lng ? haversine(origin, { lat: r.lat, lng: r.lng }) : null }));
    if (sort === "nearest") list.sort((a, b) => (a.distance ?? 99) - (b.distance ?? 99));
    if (sort === "rated") list.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    return list;
  }, [bookable, results, demo, filter, sort, origin]);

  const select = (r) => {
    setSelected(r);
    const el = cardRefs.current[r.id];
    if (el && listRef.current) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    if (panRef.current && !demo) panRef.current(r);
  };

  return (
    <section id="nearby-centres" className="light-section relative py-16 lg:py-20" data-testid="aggregator-map-section">
      <div className="dot-grid-light pointer-events-none absolute inset-0" />
      <div className="container-x relative">
        <div className="max-w-2xl">
          <Reveal><span className="eyebrow eyebrow-dark">Live Marketplace</span></Reveal>
          <Reveal delay={0.06}>
            <h2 className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-nighttext sm:text-4xl" data-testid="nearby-title">
              Find a Car Care Centre Near You
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-3 text-[15px] leading-relaxed text-[#5c5470]">
              Discover nearby car wash and detailing centres, check their location and choose where you want your car serviced.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.12}>
          <div className="mt-9 rounded-[2rem] border border-carza/20 bg-white p-4 shadow-[0_30px_70px_-35px_rgba(109,61,245,0.4)] sm:p-6" data-testid="nearby-panel">
            <p className="font-display text-lg font-bold text-nighttext sm:text-xl">Car Care. Right Around the Corner.</p>
            <p className="mt-1 text-[13px] text-[#5c5470]">CARZA connects customers with nearby car care centres and creates a smarter network for urban car care.</p>

            {/* search + controls */}
            <div className="mt-4 flex flex-col gap-3">
              <form onSubmit={(e) => { e.preventDefault(); searchArea(); }} className="flex gap-2">
                <div className="flex flex-1 items-center gap-2 rounded-xl border border-black/10 bg-[#f8f6fd] px-4">
                  <Search size={15} className="shrink-0 text-carza" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search your location or society (e.g. Sector 121, Cleo County)"
                    className="w-full bg-transparent py-3 text-sm text-nighttext outline-none placeholder:text-[#9a93a8]"
                    data-testid="nearby-search-input"
                  />
                </div>
                <button type="button" onClick={useMyLocation} className="btn btn-outline-dark shrink-0 px-4 py-3 text-[12px]" data-testid="nearby-use-location-btn">
                  <Crosshair size={14} className="text-carza" /> <span className="hidden sm:inline">Use my location</span>
                </button>
                <button type="submit" disabled={searching} className="btn btn-primary shrink-0 px-5 py-3 text-[13px] disabled:opacity-50" data-testid="nearby-search-btn">
                  {searching ? "…" : "Search"}
                </button>
              </form>
              <div className="no-scrollbar flex gap-2 overflow-x-auto pb-0.5">
                {QUICK_AREAS.map((a) => (
                  <button key={a} onClick={() => { setQuery(a); searchArea(a); }} className="shrink-0 rounded-full border border-black/[0.08] bg-lavender-2 px-3.5 py-1.5 text-[12px] font-medium text-[#3d3455] transition-colors hover:border-carza/40 hover:text-carza" data-testid={`nearby-quick-${a.toLowerCase().replace(/\s/g, "-")}`}>
                    {a}
                  </button>
                ))}
              </div>
              <div className="no-scrollbar flex items-center gap-2 overflow-x-auto">
                <SlidersHorizontal size={13} className="shrink-0 text-mist-2" />
                {[["all", "All"], ["wash", "Car Wash"], ["detailing", "Detailing"], ["bookable", "Slot Booking"], ["verified", "CARZA Verified"], ["open", "Open Now"]].map(([id, label]) => (
                  <button key={id} onClick={() => setFilter(id)} className={`shrink-0 rounded-full border px-3.5 py-1.5 text-[12px] font-semibold transition-colors ${filter === id ? "border-carza bg-carza text-white" : "border-black/[0.08] text-[#5c5470] hover:border-carza/40"}`} data-testid={`nearby-filter-${id}`}>
                    {label}
                  </button>
                ))}
                <select value={sort} onChange={(e) => setSort(e.target.value)} className="ml-auto shrink-0 rounded-full border border-black/[0.08] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#3d3455]" data-testid="nearby-sort-select">
                  <option value="nearest">Nearest</option>
                  <option value="rated">Highest Rated</option>
                </select>
              </div>
              {error && <p className="rounded-xl border border-red-300/40 bg-red-50 px-4 py-2.5 text-[13px] text-red-600" data-testid="nearby-error">{error}</p>}
            </div>

            {/* split map / list */}
            <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-12">
              <div className="lg:col-span-7">
                {hasKey() ? (
                  <div className="h-[380px] overflow-hidden rounded-3xl border border-black/[0.08] sm:h-[520px]" data-testid="live-map">
                    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
                      <GMap
                        center={center} defaultZoom={14} gestureHandling="greedy"
                        mapTypeControl={false} streetViewControl={false} fullscreenControl={false}
                        style={{ width: "100%", height: "100%" }}
                      >
                        <MapSync panRef={panRef} selected={selected} />
                        {visible.filter((r) => r.lat && r.lng).map((r) => (
                          <Marker
                            key={r.id}
                            position={{ lat: r.lat, lng: r.lng }}
                            icon={pinIcon(selected?.id === r.id, r.carza_partner)}
                            zIndex={selected?.id === r.id ? 999 : 1}
                            onClick={() => select(r)}
                            data-testid={`map-marker-${r.id}`}
                          />
                        ))}
                      </GMap>
                    </APIProvider>
                  </div>
                ) : (
                  <DemoMap count={DEMO_CENTRES.length} />
                )}
              </div>

              <div className="lg:col-span-5">
                <div ref={listRef} className="max-h-[420px] overflow-y-auto rounded-3xl border border-black/[0.06] bg-[#fbfaff] p-3 lg:h-[520px]" data-testid="nearby-list">
                  {demo && (
                    <p className="mb-3 flex items-start gap-2 rounded-xl border border-amber-300/40 bg-amber-50 px-3.5 py-2.5 text-[11.5px] leading-relaxed text-amber-800" data-testid="nearby-demo-note">
                      <MapPin size={13} className="mt-0.5 shrink-0" />
                      Demo view — real car-care centres around Noida Sectors 119–122. Live Google map, ratings, distances and open/closed status activate as soon as a Google Maps key is configured.
                    </p>
                  )}
                  {loading && <div className="flex h-40 items-center justify-center text-sm text-mist-2">Finding nearby centres…</div>}
                  {!loading && visible.length === 0 && (
                    <div className="flex h-40 flex-col items-center justify-center gap-2 text-center text-sm text-mist-2" data-testid="nearby-empty">
                      <Compass size={22} className="text-mist-2" /> No centres match this filter here yet. Try widening the filter or moving the map.
                    </div>
                  )}
                  {!loading && visible.map((r) => <CentreCard key={r.id} r={r} selected={selected?.id === r.id} onSelect={() => select(r)} refFn={(el) => (cardRefs.current[r.id] = el)} onBook={(c) => onSelectCentre && onSelectCentre(c)} />)}
                  {!demo && (
                    <p className="mt-3 text-center text-[10px] leading-relaxed text-mist-2">
                      Listing data from Google Places. “CARZA Verified” is shown only for centres onboarded by CARZA.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function MapSync({ panRef, selected }) {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    panRef.current = (r) => map.panTo({ lat: r.lat, lng: r.lng });
    return () => { panRef.current = null; };
  }, [map]);
  useEffect(() => {
    if (map && selected && selected.lat) map.panTo({ lat: selected.lat, lng: selected.lng });
  }, [selected, map]);
  return null;
}

function DemoMap({ count }) {
  return (
    <div className="dot-grid relative flex h-[380px] flex-col items-center justify-center gap-4 overflow-hidden rounded-3xl border border-carza/25 bg-gradient-to-br from-[#f3efff] to-[#e9e1ff] sm:h-[520px]" data-testid="demo-map-panel">
      {[["18%", "22%"], ["70%", "18%"], ["30%", "55%"], ["62%", "62%"], ["45%", "38%"], ["82%", "45%"]].map(([l, t], i) => (
        <span key={i} className="absolute" style={{ left: l, top: t }}>
          <span className={`block h-3.5 w-3.5 rounded-full ${i === 0 ? "bg-carza" : "bg-[#5b5470]"} ${i === 0 ? "ping-soft" : ""}`} style={{ transform: "scale(1.2)" }} />
        </span>
      ))}
      <div className="glass relative z-10 flex max-w-xs flex-col items-center gap-2 rounded-2xl px-6 py-5 text-center">
        <MapIcon size={22} className="text-carza" />
        <p className="font-display text-[15px] font-bold text-paper">Live Google Map</p>
        <p className="text-[12px] leading-relaxed text-mist">
          Configure GOOGLE_MAPS_API_KEY (backend) and REACT_APP_GOOGLE_MAPS_API_KEY (frontend) to activate real roads, markers and Places data for {count}+ centres in this area.
        </p>
      </div>
    </div>
  );
}

function CentreCard({ r, selected, onSelect, refFn, onBook }) {
  const stars = r.rating ? Math.round(r.rating) : 0;
  return (
    <button
      ref={refFn}
      onClick={onSelect}
      className={`mb-2 w-full rounded-2xl border bg-white p-3 text-left transition-all ${selected ? "border-carza shadow-[0_10px_30px_-12px_rgba(109,61,245,0.5)]" : "border-black/[0.06] hover:border-carza/40"}`}
      data-testid={`centre-card-${r.id}`}
    >
      <div className="flex gap-3">
        {r.photo ? (
          <img src={r.photo.startsWith("http") ? r.photo : `${process.env.REACT_APP_BACKEND_URL}${r.photo}`} alt={r.name} className="h-16 w-16 shrink-0 rounded-xl object-cover" loading="lazy" />
        ) : (
          <span className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-xl ${r.carza_partner ? "bg-carza-dim text-carza" : "bg-lavender-2 text-[#5b5470]"}`}>
            {r.carza_partner ? <BadgeCheck size={22} /> : <Store size={20} />}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p className="truncate text-[14px] font-bold text-nighttext">{r.name}</p>
            {r.carza_partner ? (
              <span className="shrink-0 rounded-full bg-carza px-2 py-0.5 font-mono text-[8px] font-bold uppercase tracking-wider text-white">CARZA Verified</span>
            ) : (
              <span className="shrink-0 rounded-full bg-lavender-2 px-2 py-0.5 font-mono text-[8px] uppercase tracking-wider text-[#5c5470]">{r.demo ? "Nearby Centre" : "External Centre"}</span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11.5px] text-[#5c5470]">
            {r.rating != null && !r.demo ? (
              <span className="flex items-center gap-1 font-semibold text-nighttext">
                <Star size={11} className="fill-amber-400 text-amber-400" /> {r.rating.toFixed(1)} <span className="font-normal text-mist-2">({r.rating_count})</span>
              </span>
            ) : r.distance != null ? (
              <span className="flex items-center gap-1"><MapPin size={10} /> {r.distance < 1 ? `${Math.round(r.distance * 1000)} m` : `${r.distance.toFixed(1)} km`}</span>
            ) : null}
            {!r.demo && r.address && <span className="truncate">{r.address}</span>}
            {r.demo ? <span>{r.area}</span> : r.open_now != null && (
              <span className={`flex items-center gap-1 font-semibold ${r.open_now ? "text-emerald-600" : "text-red-500"}`}>
                <Clock size={10} /> {r.open_now ? "Open now" : "Closed"}
              </span>
            )}
          </div>
          {!r.demo && <p className="mt-0.5 truncate text-[11px] text-mist-2">{r.category}</p>}
          {r.bookable && r.services && (
            <div className="mt-2 flex flex-wrap gap-1">
              {r.services.slice(0, 3).map((s) => (
                <span key={s} className="rounded-full bg-carza-dim px-2 py-0.5 text-[9px] font-semibold text-carza">{s}</span>
              ))}
            </div>
          )}
          {!r.bookable && (
            <p className="mt-1 flex items-center gap-1 text-[10.5px] text-amber-700" data-testid={`centre-availability-${r.id}`}>
              <Clock size={10} /> Availability to be confirmed
            </p>
          )}
          <div className="mt-2 flex gap-2">
            {r.bookable ? (
              <span onClick={(e) => { e.stopPropagation(); onBook(r); }} className="btn btn-primary cursor-pointer px-4 py-1.5 text-[11px]" data-testid={`centre-select-${r.id}`}>
                Select Centre &amp; Book Slot <ArrowRight size={11} />
              </span>
            ) : r.carza_partner ? (
              <span className="rounded-full bg-lavender-2 px-4 py-1.5 text-[11px] font-semibold text-[#5c5470]" data-testid={`centre-view-${r.id}`}>Availability to be confirmed</span>
            ) : r.maps_uri ? (
              <a href={r.maps_uri} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="btn btn-outline-dark px-4 py-1.5 text-[11px]" data-testid={`centre-gmaps-${r.id}`}>
                View on Google Maps <ExternalLink size={11} />
              </a>
            ) : (
              <span className="rounded-full bg-lavender-2 px-4 py-1.5 text-[11px] font-semibold text-[#5c5470]" data-testid={`centre-view-${r.id}`}>View Centre</span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

export function WhyAggregator() {
  const items = [
    { icon: Compass, title: "Discover", desc: "Find nearby car care centres." },
    { icon: Grid3X3, title: "Compare", desc: "Check location, rating and services." },
    { icon: CalendarCheck, title: "Book", desc: "Book a convenient slot at a centre — pay the centre directly after your wash." },
    { icon: ShieldCheck, title: "Care", desc: "Get a standardized CARZA experience from verified partners." },
  ];
  return (
    <section className="container-x py-6" data-testid="why-aggregator">
      <Reveal>
        <div className="rounded-[2rem] bg-white p-7 shadow-[0_20px_50px_-30px_rgba(23,19,31,0.25)] sm:p-10">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-carza-dim text-carza"><Layers size={19} /></span>
            <h2 className="font-display text-2xl font-bold text-nighttext sm:text-3xl">Why CARZA Nearby?</h2>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {items.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.06}>
                <div className="card-light flex h-full flex-col gap-3 rounded-3xl p-6" data-testid={`why-aggregator-${i}`}>
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-carza-dim text-carza"><c.icon size={18} /></span>
                  <div>
                    <h3 className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-nighttext">{c.title}</h3>
                    <p className="mt-1.5 text-[13px] leading-relaxed text-[#5c5470]">{c.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Reveal>
    </section>
  );
}

export function CentreCTA() {
  return (
    <section className="container-x py-6" data-testid="centre-cta">
      <Reveal>
        <div className="relative overflow-hidden rounded-[2rem] border border-carza/25 p-7 text-center sm:p-10" style={{ background: "linear-gradient(135deg, #6D3DF5 0%, #7C4DFF 55%, #9B7BFF 100%)" }}>
          <div className="dot-grid pointer-events-none absolute inset-0 opacity-40" />
          <div className="relative">
            <Gauge size={26} className="mx-auto text-white/90" />
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-white sm:text-4xl">Own a Car Wash Centre?</h2>
            <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-white/85 sm:text-base">
              Join the CARZA network and get discovered by customers in your area.
            </p>
            <Link to="/partners" className="btn mt-7 bg-white px-8 py-4 text-nighttext hover:bg-lavender" data-testid="centre-cta-partner-btn">
              Become a CARZA Partner <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
